import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import VehicleCard from "../components/VehicleCard";
import DatePicker from "../components/DatePicker";
import LocationSearchInput, { formatLocationLabel } from "../components/LocationSearchInput";
import { Search, Filter as FilterIcon, RotateCcw, Globe2, Car } from "lucide-react";
import { FRACTIONAL_ENABLED } from "../lib/featureFlags";
import { pageItems } from "../lib/pageItems";
import { VEHICLE_BODY_TYPES, resolveVehicleType } from "../lib/vehicleTypes";

const PARTNER_NAME = "Z-Cars partner";

const PARTNER_CATEGORIES = [
  ["economy", "Economy"], ["compact", "Compact"], ["intermediate", "Intermediate"],
  ["standard", "Standard"], ["fullsize", "Full-size"], ["premium", "Premium"],
  ["luxury", "Luxury"], ["suv", "SUV"], ["van", "Van"], ["convertible", "Convertible"], ["electric", "Electric"],
];

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookCars, setBookCars] = useState([]);
  const [bookCarsLoading, setBookCarsLoading] = useState(false);
  const [bookCarsCities, setBookCarsCities] = useState([]);
  const [traveloproCars, setTraveloproCars] = useState([]);
  const [traveloproLoading, setTraveloproLoading] = useState(false);
  const [traveloproConfigured, setTraveloproConfigured] = useState(false);
  const [traveloproCities, setTraveloproCities] = useState([]);
  const [partnerHint, setPartnerHint] = useState(null);

  const initialCity = params.get("city") || "";
  const initialPickup = params.get("pickup_address") || params.get("pickup_location") || "";
  const initialPlaceId = params.get("place_id") || "";
  const initialLat = params.get("lat") || "";
  const initialLng = params.get("lng") || "";
  const initialRadiusKm = params.get("radius_km") || "50";
  const initialHasLocation = !!(initialCity || initialPickup || initialPlaceId);

  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    city: initialCity,
    vehicle_type: resolveVehicleType(params.get("vehicle_type") || ""),
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    sort: params.get("sort") || "rating",
    pickup_address: initialPickup || initialCity,
    dropoff_address: params.get("dropoff_address") || params.get("dropoff_location") || "",
    location_id: "",
    place_id: initialPlaceId,
    location_lat: initialLat,
    location_lng: initialLng,
    location_label: initialLat && initialLng ? (initialPickup || initialCity) : "",
    radius_km: initialRadiusKm,
    airport_code: "",
    pickup_resolved_id: "",
    dropoff_resolved_id: "",
    country_res: params.get("country_res") || "IN",
    car_category: params.get("car_category") || "",
    transmission: params.get("transmission") || "",
    supplier: params.get("supplier") || "",
    driver_age: params.get("driver_age") || "25",
    currency: params.get("currency") || "CAD",
    seats: params.get("seats") || "",
    tp_sort: params.get("tp_sort") || "price_asc",
    tp_start: params.get("tp_start") || params.get("start")?.split("T")[0] || "",
    tp_end: params.get("tp_end") || params.get("end")?.split("T")[0] || "",
    investable_only: params.get("investable_only") === "true",
  });

  const [hasSearched, setHasSearched] = useState(initialHasLocation || !!params.get("q"));

  const fetchHosted = useCallback(async (f) => {
    setLoading(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(f).filter(([k, v]) => {
        if (["pickup_address", "dropoff_address", "pickup_resolved_id", "dropoff_resolved_id", "location_id", "place_id", "location_lat", "location_lng", "location_label", "radius_km", "airport_code", "country_res", "car_category", "transmission", "supplier", "driver_age", "currency", "seats", "tp_sort", "tp_start", "tp_end"].includes(k)) return false;
        if (k === "investable_only") return v === true;
        return v !== "" && v != null;
      }));
      if (f.location_lat && f.location_lng) {
        cleaned.lat = parseFloat(f.location_lat);
        cleaned.lng = parseFloat(f.location_lng);
        cleaned.radius_km = parseFloat(f.radius_km) || 50;
      }
      if (f.city) cleaned.city = f.city;
      const r = await axios.get(`${API}/vehicles`, { params: cleaned });
      setVehicles(pageItems(r.data));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  const fetchBookCars = useCallback(async (f) => {
    if (!f.city) { setBookCars([]); return; }
    setBookCarsLoading(true);
    try {
      const qp = { city: f.city, limit: 12 };
      if (f.tp_start) qp.start_date = f.tp_start;
      if (f.tp_end) qp.end_date = f.tp_end;
      const r = await axios.get(`${API}/vehicles/external`, { params: qp });
      setBookCars(r.data?.results || []);
      setBookCarsCities(r.data?.supported_cities || []);
    } catch (e) {
      setBookCars([]);
    } finally { setBookCarsLoading(false); }
  }, []);

  const fetchPartnerConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/vehicles/partners/filters`);
      const tp = (r.data?.partners || []).find(
        (p) => p.source === "Travelopro" || p.display_name === PARTNER_NAME
      );
      if (tp) setTraveloproConfigured(!!tp.configured);
    } catch (e) { /* backend down */ }
  }, []);

  const fetchPartnerFleet = useCallback(async (f) => {
    const hasLocation = !!(f.city || f.pickup_address || f.pickup_resolved_id || f.location_id || f.place_id);
    if (!hasLocation) {
      setTraveloproCars([]);
      setPartnerHint(null);
      return;
    }
    setTraveloproLoading(true);
    try {
      const qp = {
        limit: 24,
        driver_age: f.driver_age || 25,
        currency: f.currency || "CAD",
        sort: f.tp_sort || "price_asc",
        start_date: f.tp_start || undefined,
        end_date: f.tp_end || undefined,
      };
      if (f.city) qp.city = f.city;
      const pickupId = f.pickup_resolved_id && !String(f.pickup_resolved_id).startsWith("city:")
        ? f.pickup_resolved_id : null;
      const dropoffId = f.dropoff_resolved_id && !String(f.dropoff_resolved_id).startsWith("city:")
        ? f.dropoff_resolved_id : null;
      if (pickupId) qp.pickup_id = pickupId;
      else if (f.airport_code) qp.pickup_location = f.airport_code;
      else if (f.pickup_address) qp.pickup_address = f.pickup_address;
      if (dropoffId) qp.dropoff_id = dropoffId;
      else if (f.dropoff_address) qp.dropoff_address = f.dropoff_address;
      if (f.country_res) qp.country_res = f.country_res;
      if (f.q) qp.q = f.q;
      if (f.vehicle_type) qp.vehicle_type = f.vehicle_type;
      else if (f.car_category) qp.car_category = f.car_category;
      if (f.transmission) qp.transmission = f.transmission;
      if (f.min_price) qp.min_price = f.min_price;
      if (f.max_price) qp.max_price = f.max_price;
      if (f.supplier) qp.supplier = f.supplier;
      if (f.seats) qp.seats = f.seats;
      const r = await axios.get(`${API}/vehicles/external/travelopro`, { params: qp });
      setTraveloproCars(r.data?.results || []);
      setTraveloproConfigured(!!r.data?.configured);
      setTraveloproCities(r.data?.supported_cities || []);
      setPartnerHint(r.data?.integration_hint || null);
    } catch (e) {
      setTraveloproCars([]);
      setPartnerHint(`Could not reach ${PARTNER_NAME} fleet — check the backend is running and partner credentials in backend/.env`);
    } finally { setTraveloproLoading(false); }
  }, []);

  useEffect(() => {
    fetchPartnerConfig();
    fetchHosted(filters);
    if (initialHasLocation) {
      fetchBookCars(filters);
      fetchPartnerFleet(filters);
    }
  }, []); // eslint-disable-line

  const hasLocation = !!(filters.city || filters.pickup_address || filters.pickup_resolved_id || filters.location_id || filters.place_id);
  const hasGeo = !!(filters.location_lat && filters.location_lng);
  const hostedNearLabel = hasGeo && filters.location_label
    ? filters.location_label
    : (filters.city || filters.pickup_address);

  const apply = () => {
    setHasSearched(true);
    const cleaned = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v == null) return;
      if (["pickup_resolved_id", "dropoff_resolved_id", "investable_only", "location_id", "location_lat", "location_lng", "location_label", "radius_km"].includes(k)) return;
      if (k === "tp_start") {
        cleaned.tp_start = v;
        cleaned.start = v.includes("T") ? v : `${v}T10:00`;
      } else if (k === "tp_end") {
        cleaned.tp_end = v;
        cleaned.end = v.includes("T") ? v : `${v}T18:00`;
      } else if (!["tp_start", "tp_end"].includes(k)) cleaned[k] = v;
    });
    if (filters.investable_only) cleaned.investable_only = "true";
    if (filters.city && !cleaned.city) cleaned.city = filters.city;
    if (filters.pickup_address && !cleaned.pickup_address) cleaned.pickup_address = filters.pickup_address;
    if (filters.place_id) cleaned.place_id = filters.place_id;
    if (filters.location_lat) cleaned.lat = filters.location_lat;
    if (filters.location_lng) cleaned.lng = filters.location_lng;
    if (filters.location_lat && filters.location_lng) {
      cleaned.radius_km = filters.radius_km || "50";
    }
    setParams(cleaned);
    fetchHosted(filters);
    if (hasLocation || filters.city || filters.pickup_address) {
      fetchBookCars(filters);
      fetchPartnerFleet(filters);
    } else {
      setBookCars([]);
      setTraveloproCars([]);
      setPartnerHint(null);
    }
  };

  const reset = () => {
    const empty = {
      q: "", city: "", vehicle_type: "", min_price: "", max_price: "", sort: "rating",
      pickup_address: "", dropoff_address: "",
      location_id: "", place_id: "", location_lat: "", location_lng: "",
      location_label: "", radius_km: "50", airport_code: "",
      pickup_resolved_id: "", dropoff_resolved_id: "",
      country_res: "IN", car_category: "", transmission: "",
      supplier: "", driver_age: "25", currency: "CAD", seats: "", tp_sort: "price_asc",
      tp_start: "", tp_end: "", investable_only: false,
    };
    setFilters(empty);
    setParams({});
    setHasSearched(false);
    fetchHosted(empty);
    setBookCars([]);
    setTraveloproCars([]);
    setPartnerHint(null);
    fetchPartnerConfig();
  };

  const types = VEHICLE_BODY_TYPES;

  const tripStart = filters.tp_start || params.get("start")?.split("T")[0] || "";
  const tripEnd = filters.tp_end || params.get("end")?.split("T")[0] || "";
  const partnerCars = [...traveloproCars, ...bookCars];
  const partnerLoading = traveloproLoading || bookCarsLoading;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12 min-w-0" data-testid="browse-page">
      <div className="mb-10">
        <div className="eyebrow mb-2">Marketplace</div>
        <h1 className="zc-headline text-4xl md:text-5xl">The fleet</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8 min-w-0">
        <aside className="zc-card p-6 h-fit lg:sticky lg:top-24 min-w-0" data-testid="browse-filters">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 font-semibold text-sm"><FilterIcon size={14} /> Filters</div>
            <button onClick={reset} className="text-xs text-[var(--zc-text-3)] hover:text-[var(--zc-accent)] flex items-center gap-1" data-testid="filter-reset">
              <RotateCcw size={11} /> Reset
            </button>
          </div>
          <div className="space-y-4">
            <LocationSearchInput
              label="Location"
              value={filters.pickup_address || filters.city}
              country="CA"
              onChange={(text) => setFilters(f => ({
                ...f,
                pickup_address: text,
                city: text,
                location_label: "",
                pickup_resolved_id: "",
                location_id: "",
                place_id: "",
                location_lat: "",
                location_lng: "",
                airport_code: "",
              }))}
              onSelect={(loc) => setFilters(f => ({
                ...f,
                city: loc.city || f.city,
                pickup_address: formatLocationLabel(loc),
                location_label: formatLocationLabel(loc) || loc.city || "",
                location_id: loc.location_id || "",
                place_id: loc.place_id || "",
                location_lat: loc.lat != null ? String(loc.lat) : "",
                location_lng: loc.lng != null ? String(loc.lng) : "",
                airport_code: loc.airport_code || "",
                pickup_resolved_id: "",
              }))}
              testId="filter-location"
            />
            <p className="zc-meta -mt-2">
              City, airport (e.g. LAX), or street address — pick a suggestion for best results across all fleets.
            </p>
            {hasGeo && (
              <div>
                <label className="text-xs text-[var(--zc-text-3)]">Search radius</label>
                <select
                  className="zc-input h-10 px-3 w-full text-sm mt-1"
                  value={filters.radius_km}
                  onChange={(e) => setFilters((f) => ({ ...f, radius_km: e.target.value }))}
                  data-testid="filter-radius-km"
                >
                  <option value="25">25 km</option>
                  <option value="50">50 km</option>
                  <option value="100">100 km</option>
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 gap-2">
              <div className="min-w-0">
                <DatePicker
                  label="Pick-up date"
                  value={filters.tp_start}
                  onChange={(val) => setFilters((f) => ({ ...f, tp_start: val }))}
                  testid="tp-start"
                />
              </div>
              <div className="min-w-0">
                <DatePicker
                  label="Return date"
                  value={filters.tp_end}
                  onChange={(val) => setFilters((f) => ({ ...f, tp_end: val }))}
                  minDate={filters.tp_start || undefined}
                  testid="tp-end"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-[var(--zc-text-3)]">Keyword</label>
              <input className="zc-input h-10 px-3 w-full text-sm mt-1" value={filters.q}
                onChange={e => setFilters(f => ({ ...f, q: e.target.value }))}
                data-testid="filter-q" placeholder="Porsche, Tesla, Hertz…" />
            </div>
            <div>
              <label className="text-xs text-[var(--zc-text-3)] block mb-2">Vehicle type</label>
              <div className="flex flex-wrap gap-1.5">
                {types.map(([val, lbl]) => (
                  <button key={val} type="button"
                    onClick={() => setFilters(f => ({ ...f, vehicle_type: f.vehicle_type === val ? "" : val }))}
                    className={"px-3 h-8 text-xs zc-chip " + (filters.vehicle_type === val ? "zc-chip-active" : "")}
                    data-testid={`filter-type-${val}`}>{lbl}</button>
                ))}
              </div>
            </div>
            {FRACTIONAL_ENABLED && (
            <div>
              <label className="text-xs text-[var(--zc-text-3)] block mb-2">Investment</label>
              <button
                type="button"
                onClick={() => setFilters(f => ({ ...f, investable_only: !f.investable_only }))}
                className={"px-3 h-8 text-xs zc-chip " + (filters.investable_only ? "zc-chip-active" : "")}
                data-testid="filter-investable-only"
              >
                Investable only
              </button>
            </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--zc-text-3)]">Min $</label>
                <input className="zc-input h-10 px-3 w-full text-sm mt-1" type="number" value={filters.min_price}
                  onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))} data-testid="filter-min" />
              </div>
              <div>
                <label className="text-xs text-[var(--zc-text-3)]">Max $</label>
                <input className="zc-input h-10 px-3 w-full text-sm mt-1" type="number" value={filters.max_price}
                  onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))} data-testid="filter-max" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--zc-text-3)]">Sort</label>
              <select className="zc-input h-10 px-3 w-full text-sm mt-1" value={filters.sort}
                onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))} data-testid="filter-sort">
                <option value="rating">Top rated</option>
                <option value="price_asc">Price · low to high</option>
                <option value="price_desc">Price · high to low</option>
                <option value="newest">Newest</option>
              </select>
            </div>

            <button onClick={apply} className="w-full h-11 zc-btn-primary text-sm flex items-center justify-center gap-2" data-testid="filter-apply">
              <Search size={14} /> Search
            </button>
          </div>
        </aside>

        <div className="min-w-0">
          <section data-testid="hosted-section">
            <PartnerHeader
              icon={<Car size={16} />}
              title="Z-Cars hosted fleet"
              subtitle={
                loading
                  ? "Searching hosted vehicles…"
                  : `${vehicles.length} ${vehicles.length === 1 ? "vehicle" : "vehicles"}${hasLocation ? ` near ${hostedNearLabel}` : ""}`
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6" data-testid="browse-grid">
              {vehicles.map((v, i) => (
                <VehicleCard key={v.vehicle_id} v={v} index={i} startDate={tripStart} endDate={tripEnd} />
              ))}
            </div>
            {!loading && vehicles.length === 0 && (
              <div className="zc-card p-10 text-center text-[var(--zc-text-3)] mt-6" data-testid="browse-empty">
                {hasSearched && hasLocation
                  ? "No hosted vehicles in this location. Try another city or widen your filters."
                  : "No hosted vehicles match your filters."}
              </div>
            )}
          </section>

          {hasSearched && hasLocation && (
            <section className="mt-16" data-testid="external-section-partner">
              <PartnerHeader
                icon={<Globe2 size={16} />}
                title="Partner fleet"
                subtitle={
                  partnerLoading
                    ? "Searching partner inventory…"
                    : `${partnerCars.length} vehicles · global rental suppliers`
                }
                accent="var(--zc-accent)"
              />
              {partnerHint && (
                <div className="zc-card p-4 mt-4 text-sm text-[var(--zc-text-2)] border-l-4 border-[var(--zc-accent)] break-words min-w-0" data-testid="partner-hint">
                  {partnerHint}
                </div>
              )}
              {!traveloproConfigured && !partnerLoading && (
                <div className="zc-card p-6 mt-4 text-sm text-[var(--zc-text-3)]" data-testid="partner-unconfigured">
                  Partner fleet is not available yet. Contact support if this persists.
                </div>
              )}
              {!partnerLoading && partnerCars.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mt-6" data-testid="external-grid-partner">
                  {partnerCars.map((v, i) => (
                    <VehicleCard key={v.vehicle_id} v={v} index={i} startDate={tripStart} endDate={tripEnd} />
                  ))}
                </div>
              )}
              {!partnerLoading && partnerCars.length === 0 && traveloproConfigured && !partnerHint && (
                <div className="zc-card p-8 mt-4 text-center text-sm text-[var(--zc-text-3)]" data-testid="partner-empty">
                  No partner vehicles for this location. Try another address, city, airport code, or dates.
                </div>
              )}
              {!partnerLoading && filters.city && partnerCars.length === 0 && (traveloproCities.length > 0 || bookCarsCities.length > 0) && (
                <PartnerCityHint
                  city={filters.city}
                  cities={[...new Set([...traveloproCities, ...bookCarsCities])]}
                  onPick={(c) => {
                    setFilters(f => ({
                      ...f,
                      city: c,
                      pickup_address: c,
                      location_label: "",
                      pickup_resolved_id: "",
                      location_id: "",
                      place_id: "",
                      location_lat: "",
                      location_lng: "",
                      airport_code: "",
                    }));
                    setHasSearched(true);
                    const next = {
                      ...filters,
                      city: c,
                      pickup_address: c,
                      location_label: "",
                      pickup_resolved_id: "",
                      location_id: "",
                      place_id: "",
                      location_lat: "",
                      location_lng: "",
                      airport_code: "",
                    };
                    fetchHosted(next);
                    fetchBookCars(next);
                    fetchPartnerFleet(next);
                  }}
                  label="Partner fleet"
                  testId="partner"
                />
              )}
            </section>
          )}

          {hasSearched && !hasLocation && (
            <div className="zc-card p-6 mt-8 text-sm text-[var(--zc-text-3)]" data-testid="partner-needs-location">
              Enter a <strong>location</strong> above and click <strong>Search</strong> to see partner fleet results below hosted cars.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PartnerHeader({ icon, title, subtitle, accent }) {
  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 grid place-items-center rounded-lg shrink-0" style={{
        background: accent ? "rgba(225,29,72,0.12)" : "var(--zc-secondary-soft)",
        color: accent || "var(--zc-secondary)",
      }}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="zc-stat text-xl sm:text-2xl truncate">{title}</div>
        <div className="text-xs text-[var(--zc-text-3)] break-words">{subtitle}</div>
      </div>
    </div>
  );
}

function PartnerCityHint({ city, cities, onPick, label, testId }) {
  return (
    <div className="mt-8 zc-card p-6 text-sm text-[var(--zc-text-2)] min-w-0" data-testid={`${testId}-hint`}>
      <div className="font-semibold text-[var(--zc-text)] mb-1">{label}</div>
      <p className="break-words">
        No inventory in <span className="font-semibold">{city}</span>. Try:{" "}
        <span className="inline-flex flex-wrap gap-x-2 gap-y-1">
          {cities.slice(0, 6).map((c, i) => (
            <button key={c} type="button" onClick={() => onPick(c)}
              className="underline hover:text-[var(--zc-secondary)]"
              data-testid={`${testId}-suggest-${c.toLowerCase()}`}>
              {c}{i < Math.min(5, cities.length - 1) ? "," : ""}
            </button>
          ))}
        </span>
      </p>
    </div>
  );
}
